import type { GuidedTourStep } from '~/components/shared/guided-tour/index.vue';

/**
 * Composable for guided tour functionality
 */
export const useGuidedTour = () => {
  const route = useRoute();
  // This is used for getting Copy texts (en/es) value inside the script
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;

  /**
   * Helper function to build announcement text with conditional "current page"
   */
  const buildAnnouncement = (baseText: string, urlKeyword: string) => {
    const isCurrentPage = route.fullPath.includes(urlKeyword);
    if (!isCurrentPage) return baseText.replace('current page,', '');
    
    // Insert "current page" after "link," in the text
    return baseText.replace('link,', 'link, current page,');
  };

  /**
   * Get image base path based on environment
   */
  const getImageBasePath = () => {
    const isDevEnv = useRuntimeConfig().public.appEnv === 'local';
    if (isDevEnv) {
      return '/_nuxt/assets/img/';
    } else {
      return useRuntimeConfig().public.cdnURL + '/_nuxt/';
    }
  };

  /**
   * Check if device is mobile resolution
   */
  const isMobileResolution = () => {
    if (process.client && window.screen.width < 768) {
      return true;
    }
    return false;
  };

  /**
   * Build teacher guided tour steps
   */
  const buildTeacherGuidedTourSteps = (): GuidedTourStep[] => {
    const imageBasePath = getImageBasePath();
    const isMobile = isMobileResolution();

    return [
      {
        descHeading: t('TEACHER.DASHBOARD.GUIDED_TOUR.HEADING'),
        desc: t('TEACHER.DASHBOARD.GUIDED_TOUR.DESCRIPTION1'),
        image: {
          src: `${imageBasePath}shield.svg`,
          alt: ''
        }
      },
      {
        element: '#myClasses',
        announcement: buildAnnouncement(
          t('TEACHER.DASHBOARD.GUIDED_TOUR.SCREEN_READER.MY_CLASSES'),
          'dashboard'
        ),
        descHeading: ``,
        desc: t('TEACHER.DASHBOARD.GUIDED_TOUR.DESCRIPTION2'),
        position: 'bottom-left-aligned'
      },
      {
        element: '#myLibrary',
        announcement: buildAnnouncement(
          t('TEACHER.DASHBOARD.GUIDED_TOUR.SCREEN_READER.MY_LIBRARY'),
          'library'
        ),
        descHeading: ``,
        desc: t('TEACHER.DASHBOARD.GUIDED_TOUR.DESCRIPTION3'),
        position: isMobile
          ? 'bottom-middle-aligned'
          : 'bottom-left-aligned'
      },
      {
        element: '#myTraining',
        announcement: buildAnnouncement(
          t('TEACHER.DASHBOARD.GUIDED_TOUR.SCREEN_READER.MY_TRAINING'),
          'training'
        ),
        descHeading: ``,
        desc: t('TEACHER.DASHBOARD.GUIDED_TOUR.DESCRIPTION4'),
        position: isMobile
          ? 'bottom-right-aligned'
          : 'bottom-left-aligned'
      },
      {
        descHeading: ``,
        desc: t('TEACHER.DASHBOARD.GUIDED_TOUR.DESCRIPTION5'),
        image: {
          src: `${imageBasePath}guided-tour-complete.svg`,
          alt: ''
        }
      }
    ];
  };

  /**
   * Build learner/integration user guided tour steps
   */
  const buildLearnerGuidedTourSteps = (): GuidedTourStep[] => {
    const imageBasePath = getImageBasePath();

    return [
      {
        descHeading: t('LEARNER.DASHBOARD.INTEGRATION.GUIDED_TOUR.HEADING'),
        desc: t('LEARNER.DASHBOARD.INTEGRATION.GUIDED_TOUR.DESCRIPTION1'),
        image: {
          src: `${imageBasePath}shield.svg`,
          alt: ''
        }
      },
      {
        element: '[data-walkthrough="learner-walkthrough-selector"]',
        descHeading: ``,
        desc: t('LEARNER.DASHBOARD.INTEGRATION.GUIDED_TOUR.DESCRIPTION2'),
        position: 'bottom-left-aligned'
      },
      {
        descHeading: ``,
        desc: t('LEARNER.DASHBOARD.INTEGRATION.GUIDED_TOUR.DESCRIPTION5'),
        image: {
          src: `${imageBasePath}guided-tour-complete.svg`,
          alt: ''
        }
      }
    ];
  };

  return {
    buildAnnouncement,
    getImageBasePath,
    isMobileResolution,
    buildTeacherGuidedTourSteps,
    buildLearnerGuidedTourSteps
  };
};
